/*
  Koia product image lightbox.

  Survives product page transitions: transition-overlay.js swaps <main> via
  DOMParser, so inline section scripts never run again. This file initializes
  every lightbox on load and again on the `pageTransitioned` event.
*/
(() => {
  if (window.__koiaMediaLightboxBound) return;
  window.__koiaMediaLightboxBound = true;

  function initLightbox(lightbox) {
    if (lightbox.dataset.koiaLightboxReady) return;

    const sectionId = lightbox.id.replace('KoiaMediaLightbox-', '');
    const section = document.getElementById(`MainProduct-${sectionId}`);

    if (!section) return;

    lightbox.dataset.koiaLightboxReady = 'true';

    const sourceImages = Array.from(
      section.querySelectorAll('[data-koia-lightbox-image]')
    );

    const lightboxImage = lightbox.querySelector('.koia-lightbox__image');
    const closeButton = lightbox.querySelector('.koia-lightbox__close');
    const prevButton = lightbox.querySelector('.koia-lightbox__nav--prev');
    const nextButton = lightbox.querySelector('.koia-lightbox__nav--next');
    const zoomInButton = lightbox.querySelector('[data-koia-zoom-in]');
    const zoomOutButton = lightbox.querySelector('[data-koia-zoom-out]');
    const zoomLabel = lightbox.querySelector('.koia-lightbox__zoom-level');
    const stage = lightbox.querySelector('.koia-lightbox__stage');
    const currentLabel = lightbox.querySelector('[data-koia-current]');
    const totalLabel = lightbox.querySelector('[data-koia-total]');

    let currentIndex = 0;
    let zoom = 1;

    let pointerStartX = 0;
    let pointerStartY = 0;

    let swipeStartX = 0;
    let swipeStartY = 0;

    totalLabel.textContent = sourceImages.length;

    function applyZoom() {
      lightboxImage.style.transform = `scale(${zoom})`;
      zoomLabel.textContent = `${Math.round(zoom * 100)}%`;

      if (zoom > 1) {
        lightboxImage.classList.add('is-zoomed');
        stage.style.alignItems = 'flex-start';
      } else {
        lightboxImage.classList.remove('is-zoomed');
        stage.style.alignItems = 'center';
      }
    }

    function resetZoom() {
      zoom = 1;
      stage.scrollTop = 0;
      stage.scrollLeft = 0;
      applyZoom();
    }

    function loadImage(index) {
      if (!sourceImages.length) return;

      if (index < 0) {
        index = sourceImages.length - 1;
      }

      if (index >= sourceImages.length) {
        index = 0;
      }

      currentIndex = index;

      const sourceImage = sourceImages[currentIndex];

      const fullSrc =
        sourceImage.dataset.fullSrc ||
        sourceImage.currentSrc ||
        sourceImage.src;

      lightboxImage.src = fullSrc;
      lightboxImage.alt = sourceImage.alt || 'Product image';

      currentLabel.textContent = currentIndex + 1;

      resetZoom();
    }

    function openLightbox(index) {
      loadImage(index);

      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');

      document.body.style.overflow = 'hidden';

      closeButton.focus();
    }

    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');

      document.body.style.overflow = '';

      resetZoom();

      setTimeout(() => {
        lightboxImage.src = '';
      }, 200);
    }

    function nextImage() {
      loadImage(currentIndex + 1);
    }

    function previousImage() {
      loadImage(currentIndex - 1);
    }

    function zoomIn() {
      zoom = Math.min(zoom + 0.5, 3);
      applyZoom();
    }

    function zoomOut() {
      zoom = Math.max(zoom - 0.5, 1);
      applyZoom();
    }

    sourceImages.forEach((image, index) => {

      image.addEventListener('pointerdown', (event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      });

      image.addEventListener('pointerup', (event) => {
        const movedX = Math.abs(event.clientX - pointerStartX);
        const movedY = Math.abs(event.clientY - pointerStartY);

        /*
          Don't open preview if the user
          was swiping the mobile PDP carousel.
        */
        if (movedX > 10 || movedY > 10) return;

        openLightbox(index);
      });

    });

    closeButton.addEventListener('click', closeLightbox);

    prevButton.addEventListener('click', (event) => {
      event.stopPropagation();
      previousImage();
    });

    nextButton.addEventListener('click', (event) => {
      event.stopPropagation();
      nextImage();
    });

    zoomInButton.addEventListener('click', (event) => {
      event.stopPropagation();
      zoomIn();
    });

    zoomOutButton.addEventListener('click', (event) => {
      event.stopPropagation();
      zoomOut();
    });

    lightboxImage.addEventListener('click', (event) => {
      event.stopPropagation();

      if (zoom === 1) {
        zoom = 2;
      } else {
        zoom = 1;
      }

      applyZoom();
    });

    stage.addEventListener(
      'touchstart',
      (event) => {
        if (!event.touches.length) return;

        const touch = event.touches[0];

        swipeStartX = touch.clientX;
        swipeStartY = touch.clientY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (zoom > 1) return;
        if (!event.changedTouches.length) return;

        const touch = event.changedTouches[0];

        const deltaX = touch.clientX - swipeStartX;
        const deltaY = touch.clientY - swipeStartY;

        if (
          Math.abs(deltaX) > 50 &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          if (deltaX < 0) {
            nextImage();
          } else {
            previousImage();
          }
        }
      },
      { passive: true }
    );

    lightbox.addEventListener('click', (event) => {
      if (
        event.target === lightbox ||
        event.target === stage
      ) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', function onKeydown(event) {
      if (!lightbox.isConnected) {
        document.removeEventListener('keydown', onKeydown);
        return;
      }

      if (!lightbox.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        closeLightbox();
      }

      if (event.key === 'ArrowRight') {
        nextImage();
      }

      if (event.key === 'ArrowLeft') {
        previousImage();
      }

      if (event.key === '+' || event.key === '=') {
        zoomIn();
      }

      if (event.key === '-') {
        zoomOut();
      }
    });
  }

  function initAll() {
    document.querySelectorAll('.koia-lightbox[id^="KoiaMediaLightbox-"]').forEach(initLightbox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('pageTransitioned', () => {
    document.body.style.overflow = '';
    initAll();
  });
})();
